import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Label, Form, FormGroup, Row, Col } from "reactstrap";
import { createToll, updateToll } from "../../../Services/ServiceCity.service";
import { showAlert } from "../../../Services/Shared.service";

const TollForm = ({ cityId, editingToll, toggleModal, onTollSaved, fetchTolls }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      toll_name: "",
      kml: "",
      charge: "",
    },
  });

  useEffect(() => {
    if (editingToll) {
      reset({
        toll_name: editingToll.tollName || "",
        kml: "",
        charge: editingToll.charge !== undefined ? editingToll.charge : "",
      });

      if (editingToll.kml) {
        try {
          const fileName = `${editingToll.tollName || 'Existing_Toll'}.kml`;
          const file = new File([editingToll.kml], fileName, { type: 'application/vnd.google-earth.kml+xml' });
          const dt = new DataTransfer();
          dt.items.add(file);
          setValue("kml", dt.files);
          setTimeout(() => {
            const fileInput = document.getElementById("kml-input-toll");
            if (fileInput) fileInput.files = dt.files;
          }, 0);
        } catch (e) {
          console.error("Error setting file input", e);
        }
      }
    } else {
      reset({
        toll_name: "",
        kml: "",
        charge: "",
      });
    }
  }, [editingToll, reset]);

  const onSubmit = async (formValues) => {
    setIsSubmitting(true);
    const tollName = editingToll ? editingToll.tollName : formValues.toll_name;
    
    const requestPayload = {
      tollName: tollName,
      charge: parseFloat(formValues.charge),
    };
    const requestBlob = new Blob([JSON.stringify(requestPayload)], { type: 'application/json' });
    
    const formData = new FormData();
    formData.append("request", requestBlob);

    if (formValues.kml && formValues.kml.length > 0 && typeof formValues.kml !== "string") {
      formData.append("kmlFile", formValues.kml[0]);
    }

    try {
      const idToUpdate = editingToll ? editingToll.suburbId : null;
      if (editingToll && idToUpdate) {
        const res = await updateToll(cityId, idToUpdate, formData);
        const updatedToll = res?.data?.data || {
          ...editingToll,
          tollName: tollName,
          charge: parseFloat(formValues.charge),
        };
        onTollSaved(updatedToll, true);
        showAlert("success", "Toll updated successfully");
      } else {
        await createToll(cityId, formData);
        fetchTolls(); // Required to retrieve the database-generated suburbId for the new toll
        showAlert("success", "Toll created successfully");
      }
      toggleModal();
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Something went wrong";
      showAlert("error", errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>Toll Gate Name <span style={{ color: "red" }}>*</span></Label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. Mundka Toll Plaza"
              disabled={!!editingToll}
              {...register("toll_name", {
                required: "Toll Gate Name is required",
                onChange: (e) => setValue("toll_name", e.target.value.trimStart()),
              })}
            />
            {errors.toll_name && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.toll_name.message}</span>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>Toll Charge <span style={{ color: "red" }}>*</span></Label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              placeholder="e.g. 80.00"
              {...register("charge", {
                required: "Toll Charge is required",
                min: { value: 0, message: "Toll Charge cannot be negative" },
              })}
            />
            {errors.charge && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.charge.message}</span>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="12">
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>KML Boundary File {!editingToll && <span style={{ color: "red" }}>*</span>}</Label>
            {editingToll && <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>Upload a new file to replace the existing KML boundary.</div>}
            <input
              id="kml-input-toll"
              type="file"
              accept=".kml,.xml"
              className="form-control"
              {...register("kml", {
                required: editingToll ? false : "KML boundary file is required",
              })}
            />
            {errors.kml && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.kml.message}</span>
            )}
          </FormGroup>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2" style={{ marginTop: "20px" }}>
        <button
          type="button"
          className="btn btn-light"
          onClick={toggleModal}
          disabled={isSubmitting}
          style={{ textTransform: "none" }}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn"
          disabled={isSubmitting}
          style={{
            backgroundColor: "rgba(57, 181, 74, 1)",
            color: "white",
            textTransform: "none",
            fontWeight: "600",
          }}
        >
          {isSubmitting ? "Saving..." : editingToll ? "Update" : "Create"}
        </button>
      </div>
    </Form>
  );
};

export default TollForm;
